import { DialogRef } from '@angular/cdk/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import {
  ProviderInvitation,
  ProviderInvitationAccessType,
  ProviderSubscriptionCatalog,
} from '@models';
import {
  ProviderInvitationApiService,
  ProviderSubscriptionCatalogApiService,
  ToastService,
} from '@services';
import {
  ButtonComponent,
  DropdownSelectComponent,
  InputValidationMessageDirective,
  SelectOptionComponent,
  SpinnerComponent,
  phoneNumberValidator,
  triggerValidation,
} from '@ui-lib';
import { catchError, EMPTY, finalize } from 'rxjs';

const ProviderInvitationUserAlreadyExistsErrorKey =
  'PROVIDER_INVITATION_USER_ALREADY_EXISTS';
const ProviderInvitationInvalidPlanErrorKey =
  'PROVIDER_INVITATION_INVALID_PLAN';

type BillingMode = 'FreeGrant' | 'PaidSubscription';

interface PlanOption {
  priceId: string;
  productId: string;
  label: string;
}

interface CouponOption {
  value: string;
  label: string;
}

const CURRENCY_FORMATTERS = new Map<string, Intl.NumberFormat>();

function formatAmount(amount: number, currency: string): string {
  const code = currency.toUpperCase();
  let formatter = CURRENCY_FORMATTERS.get(code);
  if (!formatter) {
    formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
    });
    CURRENCY_FORMATTERS.set(code, formatter);
  }
  return formatter.format(amount / 100);
}

@Component({
  selector: 'admin-portal-add-provider-dialog',
  imports: [
    ButtonComponent,
    DropdownSelectComponent,
    InputValidationMessageDirective,
    MatIconModule,
    ReactiveFormsModule,
    SelectOptionComponent,
    SpinnerComponent,
  ],
  templateUrl: './add-provider-dialog.component.html',
})
export class AddProviderDialogComponent implements OnInit {
  private readonly dialogRef = inject(DialogRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly invitationService = inject(ProviderInvitationApiService);
  private readonly catalogService = inject(
    ProviderSubscriptionCatalogApiService
  );
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  readonly submitting = signal(false);
  readonly catalogLoading = signal(false);
  readonly catalogFailed = signal(false);
  private readonly catalog = signal<ProviderSubscriptionCatalog | null>(null);

  readonly accessTypeOptions: {
    value: ProviderInvitationAccessType;
    label: string;
  }[] = [
    { value: 'Trial2Day', label: 'Trial 2 days' },
    { value: 'Trial30Day', label: 'Trial 30 days' },
    { value: 'Annual12Month', label: 'Annual 12 months' },
  ];

  readonly trialOptions: {
    value: ProviderInvitationAccessType;
    label: string;
  }[] = [
    { value: 'Trial14Day', label: '14-day trial' },
    { value: 'Trial30Day', label: '30-day trial' },
    { value: 'Annual12Month', label: 'No trial' },
  ];

  readonly form = this.formBuilder.nonNullable.group({
    firstName: ['', [Validators.required, Validators.maxLength(100)]],
    lastName: ['', [Validators.required, Validators.maxLength(100)]],
    email: [
      '',
      [Validators.required, Validators.email, Validators.maxLength(200)],
    ],
    phone: [
      '',
      [Validators.required, phoneNumberValidator(), Validators.maxLength(20)],
    ],
    billingMode: ['FreeGrant' as BillingMode, [Validators.required]],
    accessType: ['', [Validators.required]],
    stripePriceId: [''],
    stripeCouponId: [''],
    trialAccessType: ['Trial14Day' as ProviderInvitationAccessType],
  });

  private readonly billingMode = toSignal(
    this.form.controls.billingMode.valueChanges,
    { initialValue: this.form.controls.billingMode.value }
  );

  readonly isPaid = computed(() => this.billingMode() === 'PaidSubscription');

  readonly planOptions = computed<PlanOption[]>(() => {
    const catalog = this.catalog();
    if (!catalog) return [];
    return catalog.products.flatMap(product =>
      product.prices.map(price => ({
        priceId: price.id,
        productId: product.id,
        label: this.planLabel(product.name, price),
      }))
    );
  });

  readonly couponOptions = computed<CouponOption[]>(() => {
    const catalog = this.catalog();
    if (!catalog) return [{ value: '', label: 'No coupon' }];
    return [
      { value: '', label: 'No coupon' },
      ...catalog.coupons.map(coupon => ({
        value: coupon.id,
        label: this.couponLabel(coupon.name ?? coupon.id, coupon),
      })),
    ];
  });

  get emailControl() {
    return this.form.controls.email;
  }

  get planControl() {
    return this.form.controls.stripePriceId;
  }

  ngOnInit(): void {
    this.applyBillingModeValidators('FreeGrant');
    this.form.controls.billingMode.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(mode => {
        this.applyBillingModeValidators(mode);
        if (mode === 'PaidSubscription') this.loadCatalog();
      });
  }

  setBillingMode(mode: BillingMode): void {
    this.form.controls.billingMode.setValue(mode);
  }

  private applyBillingModeValidators(mode: BillingMode): void {
    const { accessType, stripePriceId, trialAccessType } = this.form.controls;
    if (mode === 'FreeGrant') {
      accessType.setValidators([Validators.required]);
      stripePriceId.clearValidators();
      trialAccessType.clearValidators();
    } else {
      accessType.clearValidators();
      stripePriceId.setValidators([Validators.required]);
      trialAccessType.setValidators([Validators.required]);
    }
    accessType.updateValueAndValidity();
    stripePriceId.updateValueAndValidity();
    trialAccessType.updateValueAndValidity();
  }

  private loadCatalog(): void {
    if (this.catalog() || this.catalogLoading()) return;
    this.catalogLoading.set(true);
    this.catalogFailed.set(false);
    this.catalogService
      .getCatalog()
      .pipe(
        catchError(() => {
          this.catalogFailed.set(true);
          this.toast.showWarning(
            'Could not load the plan catalog. Please try again.'
          );
          return EMPTY;
        }),
        finalize(() => this.catalogLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(catalog => this.catalog.set(catalog));
  }

  submit(): void {
    this.form.markAllAsTouched();
    triggerValidation(this.form);
    if (this.form.invalid) return;

    this.clearEmailServerError();
    this.clearPlanServerError();

    const raw = this.form.getRawValue();
    const isPaid = raw.billingMode === 'PaidSubscription';

    const body: ProviderInvitation = {
      firstName: raw.firstName,
      lastName: raw.lastName,
      email: raw.email,
      phone: raw.phone || undefined,
      accessType: isPaid
        ? raw.trialAccessType
        : (raw.accessType as ProviderInvitationAccessType),
    };

    if (isPaid) {
      const plan = this.planOptions().find(
        option => option.priceId === raw.stripePriceId
      );
      body.stripePriceId = plan?.priceId;
      body.stripeProductId = plan?.productId;
      body.stripeCouponId = raw.stripeCouponId || undefined;
    }

    this.submitting.set(true);

    this.invitationService
      .inviteProvider(body)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (
            error.error?.code === ProviderInvitationUserAlreadyExistsErrorKey
          ) {
            this.emailControl.setErrors({
              ...this.emailControl.errors,
              providerInvitationUserAlreadyExists: true,
            });
            this.emailControl.markAsTouched();
            return EMPTY;
          }

          if (error.error?.code === ProviderInvitationInvalidPlanErrorKey) {
            this.planControl.setErrors({
              ...this.planControl.errors,
              providerInvitationInvalidPlan: true,
            });
            this.planControl.markAsTouched();
            return EMPTY;
          }

          this.toast.showWarning('Something went wrong. Please try again.');
          return EMPTY;
        }),
        finalize(() => this.submitting.set(false))
      )
      .subscribe(() => {
        this.dialogRef.close();
        this.toast.showSuccess(`Invite sent to ${raw.email}`);
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private planLabel(
    productName: string | null,
    price: {
      nickname: string | null;
      unitAmount: number | null;
      currency: string | null;
      interval: string | null;
    }
  ): string {
    const name = productName ?? price.nickname ?? 'Plan';
    if (price.unitAmount == null || !price.currency) return name;
    const amount = formatAmount(price.unitAmount, price.currency);
    return price.interval
      ? `${name} — ${amount} / ${price.interval}`
      : `${name} — ${amount}`;
  }

  private couponLabel(
    name: string,
    coupon: {
      percentOff: number | null;
      amountOff: number | null;
      currency: string | null;
    }
  ): string {
    if (coupon.percentOff != null) return `${name} (${coupon.percentOff}% off)`;
    if (coupon.amountOff != null && coupon.currency) {
      return `${name} (${formatAmount(coupon.amountOff, coupon.currency)} off)`;
    }
    return name;
  }

  private clearEmailServerError(): void {
    const errors = this.emailControl.errors;
    if (!errors?.['providerInvitationUserAlreadyExists']) {
      return;
    }

    const { providerInvitationUserAlreadyExists, ...remainingErrors } = errors;
    void providerInvitationUserAlreadyExists;
    this.emailControl.setErrors(
      Object.keys(remainingErrors).length > 0 ? remainingErrors : null
    );
  }

  private clearPlanServerError(): void {
    const errors = this.planControl.errors;
    if (!errors?.['providerInvitationInvalidPlan']) {
      return;
    }

    const { providerInvitationInvalidPlan, ...remainingErrors } = errors;
    void providerInvitationInvalidPlan;
    this.planControl.setErrors(
      Object.keys(remainingErrors).length > 0 ? remainingErrors : null
    );
  }
}
